import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getStoredPairs, removePair } from '../utils/pairing';
import { publicKeyToBip32Words } from '../utils/bip32';
import { useIdentity } from '../hooks/useIdentity';
import type { PairConfig } from '../types/identity';

export function PairManagementScreen(): JSX.Element {
  const { identity } = useIdentity();
  const [pairs, setPairs] = useState<PairConfig[]>([]);
  const [showWords, setShowWords] = useState<Set<string>>(new Set());
  const [visibleWordIndices, setVisibleWordIndices] = useState<Map<string, Set<number>>>(new Map());

  useEffect(() => {
    const stored = getStoredPairs();
    setPairs(stored);
  }, []);

  const handleRemove = (pairUuid: string): void => {
    if (confirm('Voulez-vous vraiment supprimer ce pair ?')) {
      removePair(pairUuid);
      setPairs(getStoredPairs());
      setShowWords((prev) => {
        const next = new Set(prev);
        next.delete(pairUuid);
        return next;
      });
    }
  };

  const handleToggleWords = (pairUuid: string): void => {
    setShowWords((prev) => {
      const next = new Set(prev);
      if (next.has(pairUuid)) {
        next.delete(pairUuid);
        // Reset visible words when hiding
        setVisibleWordIndices((prevVisible) => {
          const nextVisible = new Map(prevVisible);
          nextVisible.delete(pairUuid);
          return nextVisible;
        });
      } else {
        next.add(pairUuid);
      }
      return next;
    });
  };

  const handleToggleWordVisibility = (pairUuid: string, wordIndex: number): void => {
    setVisibleWordIndices((prev) => {
      const next = new Map(prev);
      const pairVisible = next.get(pairUuid) ?? new Set<number>();
      const newPairVisible = new Set(pairVisible);
      if (newPairVisible.has(wordIndex)) {
        newPairVisible.delete(wordIndex);
      } else {
        newPairVisible.add(wordIndex);
      }
      next.set(pairUuid, newPairVisible);
      return next;
    });
  };

  const getWordsForPair = (pair: PairConfig): string[] | null => {
    if (pair.publicKey !== undefined) {
      return publicKeyToBip32Words(pair.publicKey);
    }
    if (pair.is_local && identity !== null && identity.publicKey !== undefined) {
      return publicKeyToBip32Words(identity.publicKey);
    }
    return null;
  };

  const getPublicKeyForPair = (pair: PairConfig): string | null => {
    if (pair.is_local && identity !== null) {
      return identity.publicKey;
    }
    return pair.publicKey ?? null;
  };

  return (
    <main>
      <h1>Gérer les pairs</h1>
      <p>
        <Link to="/">← Accueil</Link>
      </p>

      {pairs.length === 0 ? (
        <p>Aucun pair configuré.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {pairs.map((pair) => {
            const words = getWordsForPair(pair);
            const publicKey = getPublicKeyForPair(pair);
            const isShowingWords = showWords.has(pair.uuid);

            if (words === null) {
              return null;
            }

            return (
              <li
                key={pair.uuid}
                style={{
                  border: '1px solid #ccc',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1rem',
                  backgroundColor: '#f9f9f9',
                }}
              >
                <div>
                  <h3>
                    {pair.is_local ? 'Pair local' : 'Pair distant'}
                    {pair.can_sign ? ' (peut signer)' : ' (ne peut pas signer)'}
                  </h3>
                  <p>
                    <strong>UUID:</strong>{' '}
                    <span style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                      {pair.uuid}
                    </span>
                  </p>
                  {publicKey !== null && (
                    <p>
                      <strong>Clé publique:</strong>{' '}
                      <span style={{ fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>
                        {publicKey}
                      </span>
                    </p>
                  )}
                  {isShowingWords && (
                    <div
                      style={{
                        marginTop: '0.5rem',
                        padding: '0.75rem',
                        backgroundColor: '#fff',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                      }}
                    >
                      <p>
                        <strong>Mots (17):</strong>
                      </p>
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '0.5rem',
                          marginTop: '0.5rem',
                        }}
                      >
                        {words.map((word, wordIndex) => {
                          const visible = visibleWordIndices.get(pair.uuid)?.has(wordIndex) ?? false;
                          return (
                            <div
                              key={wordIndex}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                                padding: '0.25rem 0.5rem',
                                backgroundColor: '#f5f5f5',
                                borderRadius: '4px',
                                border: '1px solid #ddd',
                              }}
                            >
                              <span style={{ fontSize: '0.75rem', color: '#666', fontWeight: 'bold' }}>
                                {wordIndex + 1}:
                              </span>
                              <span
                                style={{
                                  fontFamily: 'monospace',
                                  fontSize: '0.9rem',
                                  minWidth: '80px',
                                }}
                              >
                                {visible ? `${wordIndex + 1}. ${word}` : '•••••'}
                              </span>
                              <button
                                type="button"
                                onClick={() => handleToggleWordVisibility(pair.uuid, wordIndex)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  padding: '0.125rem',
                                  fontSize: '0.875rem',
                                  color: '#666',
                                }}
                                aria-label={`${visible ? 'Masquer' : 'Afficher'} le mot ${wordIndex + 1}`}
                                title={`${visible ? 'Masquer' : 'Afficher'} le mot ${wordIndex + 1}`}
                              >
                                {visible ? '👁' : '👁‍🗨'}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={() => handleToggleWords(pair.uuid)}
                      style={{ padding: '0.5rem 1rem' }}
                    >
                      {isShowingWords ? 'Masquer les mots' : 'Afficher les mots'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemove(pair.uuid)}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#dc3545',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </main>
  );
}
